import { useCallback, useEffect, useMemo, useState } from 'react'
import { intl } from '../../../../../i18n'
import { ExtendSelectOption } from '../../search-components/wbs-item-search-components/TagMultiAutocompleteCondition'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTagsByProject } from '../../../../../store/tag'
import { AllState } from '../../../../../store'
import { TagForWbsItem } from '../../../../../lib/functions/tag'
import {
  TextMatchCondition,
  TextMatchMode,
} from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { SearchItemLabel, usePopup } from '..'
import { Chip, Popover } from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import checkboxInactiveSIcon from '../../../../../assets/checkbox_inactive_s.svg'
import checkboxActiveSIcon from '../../../../../assets/checkbox_active_s.svg'
import checkboxExcludeSIcon from '../../../../../assets/checkbox_exclude_s.svg'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import { Text } from '../../../../components/typography'

const chipColor: Record<
  TextMatchMode,
  { background: string; color: string; border: string }
> = {
  include: {
    background: colorPalette.skyBlue[1],
    color: colorPalette.skyBlue[8],
    border: colorPalette.skyBlue[7],
  },
  exclude: {
    background: colorPalette.pink[0],
    color: colorPalette.pink[6],
    border: colorPalette.pink[5],
  },
}

const triStateIcon: Record<string, string> = {
  include: checkboxActiveSIcon,
  exclude: checkboxExcludeSIcon,
}

const nextTriState = (
  current: TextMatchMode | undefined
): TextMatchMode | undefined => {
  switch (current) {
    case undefined:
      return 'include'
    case 'include':
      return 'exclude'
    case 'exclude':
      return undefined
  }
}

const ChipRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const OptionRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const OptionLabel = styled(Text)({
  margin: 'auto 10px',
})

const ExcludeOptionLabel = styled(Text)({
  margin: 'auto 10px',
  textDecoration: 'line-through',
  color: colorPalette.monotone[4],
})

const TriStateCheckboxButton = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3px',
  marginRight: '8px',
})

const PopoverContent = styled('div')({
  padding: '10px',
  minWidth: '200px',
  maxHeight: '300px',
  overflowY: 'auto',
})

type BaseProps = {
  isLoading?: boolean
  search: () => void
  onClose?: () => void
}

type TriStateTagProps = BaseProps & {
  triState: true
  value: TextMatchCondition[] | undefined
  onChange: (value: TextMatchCondition[] | undefined) => void
}

type StandardTagProps = BaseProps & {
  triState?: false
  value: string[] | undefined
  onChange: (value: string[] | undefined) => void
}

type TagMultiAutocompleteConditionItemLabelProps =
  | TriStateTagProps
  | StandardTagProps

export const TagMultiAutocompleteConditionItemLabel = (
  props: TagMultiAutocompleteConditionItemLabelProps
) => {
  const { isLoading, search, onClose } = props
  const dispatch = useDispatch()
  const { projectUuid } = useProjectPrivateContext()
  const tags = useSelector<AllState, TagForWbsItem[] | undefined>(state =>
    projectUuid ? state.tag[projectUuid] : undefined
  )
  const [options, setOptions] = useState<ExtendSelectOption[]>([])
  useEffect(() => {
    if (projectUuid && options?.length === 0) {
      dispatch(fetchTagsByProject(projectUuid))
    }
  }, [projectUuid])
  useEffect(() => {
    if (!tags || tags.length === 0) return
    setOptions(
      tags.map(v => ({
        value: v.uuid,
        name: v.name,
        backgroundColor: v.backgroundColor,
      }))
    )
  }, [tags])

  if (props.triState) {
    return (
      <TriStateTagItemLabel
        value={props.value}
        onChange={props.onChange}
        options={options}
        isLoading={isLoading}
        search={search}
        onClose={onClose}
      />
    )
  }
  return (
    <StandardTagItemLabel
      value={props.value}
      onChange={props.onChange}
      options={options}
      isLoading={isLoading}
      search={search}
      onClose={onClose}
    />
  )
}

type StandardTagItemLabelProps = {
  value: string[] | undefined
  onChange: (value: string[] | undefined) => void
  options: ExtendSelectOption[]
  isLoading?: boolean
  search: () => void
  onClose?: () => void
}

const StandardTagItemLabel = ({
  value,
  onChange,
  options,
  isLoading,
  search,
  onClose,
}: StandardTagItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const selectedOptions = useMemo(() => {
    return options.filter(op => (value || []).includes(op.value))
  }, [value, options])

  const valueNames = useMemo(() => {
    return selectedOptions.map(op => op.name)
  }, [selectedOptions])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose?.()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const onToggleValue = useCallback(
    (optionValue: string) => {
      const newValue = [...(value || [])]
      if (newValue.includes(optionValue)) {
        newValue.splice((value || []).indexOf(optionValue), 1)
      } else {
        newValue.push(optionValue)
      }
      onChange(newValue)
      if (newValue.length === 0) {
        search()
        closePopup()
        onClose?.()
      }
    },
    [value, onChange, search, closePopup, onClose]
  )

  if (selectedOptions.length === 0) return <></>
  const header = intl.formatMessage({ id: 'wbsItemSearchFilterPanel.tags' })
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {header}: {valueNames.join(',')}
      </SearchItemLabel>
      <Popover
        open={open && !isLoading}
        anchorEl={ref.current || null}
        onClose={onClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          {options.map(option => (
            <OptionRow
              key={option.value}
              onClick={() => onToggleValue(option.value)}
            >
              <Checkbox
                sx={{ marginRight: '8px' }}
                checked={(value || []).some(v => v === option.value)}
              />
              <OptionLabel color={colorPalette.monotone[4]}>
                {option.name}
              </OptionLabel>
            </OptionRow>
          ))}
        </PopoverContent>
      </Popover>
    </>
  )
}

type TriStateTagItemLabelProps = {
  value: TextMatchCondition[] | undefined
  onChange: (value: TextMatchCondition[] | undefined) => void
  options: ExtendSelectOption[]
  isLoading?: boolean
  search: () => void
  onClose?: () => void
}

const TriStateTagItemLabel = ({
  value,
  onChange,
  options,
  isLoading,
  search,
  onClose,
}: TriStateTagItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const getOptionName = useCallback(
    (uuid: string) => options.find(o => o.value === uuid)?.name ?? uuid,
    [options]
  )

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose?.()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const onTriStateChange = useCallback(
    (optionValue: string) => {
      const existing = value?.find(c => c.value === optionValue)
      const currentMode = existing?.mode
      const next = nextTriState(currentMode)

      if (next === undefined) {
        const filtered = (value ?? []).filter(c => c.value !== optionValue)
        const newValue = filtered.length > 0 ? filtered : undefined
        onChange(newValue)
        if (!newValue) {
          search()
          closePopup()
          onClose?.()
        }
      } else if (existing) {
        const updated = (value ?? []).map(c =>
          c.value === optionValue ? { ...c, mode: next } : c
        )
        onChange(updated)
      } else {
        onChange([...(value ?? []), { value: optionValue, mode: next }])
      }
    },
    [value, onChange, search, closePopup, onClose]
  )

  const getTriStateMode = useCallback(
    (optionValue: string): TextMatchMode | undefined => {
      return value?.find(c => c.value === optionValue)?.mode
    },
    [value]
  )

  const header = intl.formatMessage({ id: 'wbsItemSearchFilterPanel.tags' })

  if (!value || value.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        isLoading={isLoading}
        onClick={openPopup}
        onClear={onClear}
      >
        <ChipRow>
          {header}:
          {value.map((c, i) => {
            const style = chipColor[c.mode]
            return (
              <Chip
                key={i}
                label={getOptionName(c.value)}
                size="small"
                sx={{
                  height: '20px',
                  backgroundColor: style.background,
                  color: style.color,
                  border: `1px solid ${style.border}`,
                  textDecoration:
                    c.mode === 'exclude' ? 'line-through' : 'none',
                  '& .MuiChip-label': { padding: '0 6px' },
                }}
              />
            )
          })}
        </ChipRow>
      </SearchItemLabel>
      <Popover
        open={open && !isLoading}
        anchorEl={ref.current || null}
        onClose={onClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          {options.map(option => {
            const mode = getTriStateMode(option.value)
            const isExclude = mode === 'exclude'
            const iconSrc = mode ? triStateIcon[mode] : checkboxInactiveSIcon
            const Label = isExclude ? ExcludeOptionLabel : OptionLabel

            return (
              <OptionRow
                key={option.value}
                onClick={() => onTriStateChange(option.value)}
              >
                <TriStateCheckboxButton>
                  <img src={iconSrc} width={18} height={18} />
                </TriStateCheckboxButton>
                <Label color={colorPalette.monotone[4]}>{option.name}</Label>
              </OptionRow>
            )
          })}
        </PopoverContent>
      </Popover>
    </>
  )
}
